import type {Analytics} from '../../core/Analytics';
import {HeartbeatService} from '../../core/HeartbeatService';
import {Event} from '../../enums/Event';
import {Player} from '../../enums/Player';
import {StreamTypes} from '../../enums/StreamTypes';
import {ErrorDetailBackend} from '../../features/errordetails/ErrorDetailBackend';
import {ErrorDetailTracking} from '../../features/errordetails/ErrorDetailTracking';
import type {Feature} from '../../features/Feature';
import type {FeatureConfig} from '../../features/FeatureConfig';
import type {FeatureConfigContainer} from '../../types/FeatureConfigContainer';
import {normalizeVideoDuration, PlaybackInfo} from '../../types/PlaybackInfo';
import {InternalAdapter} from '../internal/InternalAdapter';
import type {InternalAdapterAPI} from '../internal/InternalAdapterAPI';

import {BitmovinPwxAnalyticsStateMachine} from './BitmovinPwxAnalyticsStateMachine';
import {
  CoreExportNames,
  PlaybackApiEventsPackageExportNames,
  SourceExportNames,
  SourcesApiExportNames,
  StreamingState,
} from './ExportNames';
import {AnalyticsContext} from './Types';

const PWX_ABORT_ERROR_CODE = 1007;

export class BitmovinPwxInternalAdapter extends InternalAdapter implements InternalAdapterAPI {
  private readonly context: AnalyticsContext;

  /**
   * This flag ensures whether the "unload path" has already run once.
   *
   * The "unload path" is wired to three listeners (`destroy`, `beforeunload`, `unload`) that can all fire during teardown.
   */
  private hasHandledUnload = false;

  constructor(context: AnalyticsContext) {
    super();
    this.context = context;
    const playingHeartbeatService = new HeartbeatService(() => this.currentTime);
    const stateMachine = new BitmovinPwxAnalyticsStateMachine(this.stateMachineCallbacks, playingHeartbeatService);
    playingHeartbeatService.setListener(stateMachine);
    this.stateMachine = stateMachine;
  }

  private getSource() {
    return this.context.registry.get(SourceExportNames.SourceState);
  }

  private getStream() {
    return this.context.registry.get(StreamingState).stream;
  }

  protected get currentTime(): number {
    return this.getSource().playback.playhead.position;
  }

  getAutoPlay(): boolean {
    return this.getSource().playbackConfig.autoplay;
  }

  getCurrentPlaybackInfo(): PlaybackInfo {
    const hls = this.getSource().sourceConfig.resources.filter((v) => v.url.includes('.m3u8'))[0]?.url;
    const dash = this.getSource().sourceConfig.resources.filter((v) => v.url.includes('.mpd'))[0]?.url;

    const videoRect = this.getSource().video.element?.getBoundingClientRect();
    const videoWidth = Math.round(videoRect?.width ?? 1);
    const videoHeight = Math.round(videoRect?.height ?? 1);

    let streamFormat: StreamTypes | undefined = undefined;
    if (hls) {
      streamFormat = StreamTypes.hls;
    } else if (dash) {
      streamFormat = StreamTypes.dash;
    }

    // height, width, codecs and bitrate are fixed until we have quality api
    return {
      ...this.getCommonPlaybackInfo(),
      droppedFrames: 0,
      isMuted: this.getSource().video.element?.muted,
      isLive: this.getStream().type === 'live',
      videoWindowHeight: videoHeight,
      videoWindowWidth: videoWidth,
      videoDuration: normalizeVideoDuration(this.getSource().video.element?.duration),
      videoPlaybackHeight: videoHeight,
      videoPlaybackWidth: videoWidth,
      m3u8Url: hls,
      mpdUrl: dash,
      streamFormat,
      videoCodec: undefined, // not tracked yet
      audioCodec: undefined, // not tracked yet
      videoBitrate: 0, // not tracked yet
      audioBitrate: 0, // not tracked yet
    };
  }

  getPlayerName(): string {
    return Player.BITMOVIN_PWX;
  }

  getPlayerTech(): string {
    return 'webx:html5';
  }

  getPlayerVersion(): string {
    return this.context.registry.get(CoreExportNames.Constants).Version;
  }

  initialize(analytics: Analytics): Array<Feature<FeatureConfigContainer, FeatureConfig>> {
    this.registerEventListeners();
    this.registerUnloadEventListeners();

    const errorDetailTracking = new ErrorDetailTracking(
      analytics.errorDetailTrackingSettingsProvider,
      new ErrorDetailBackend(analytics.errorDetailTrackingSettingsProvider.collectorConfig),
      [analytics.errorDetailSubscribable],
      undefined,
    );

    return [errorDetailTracking];
  }

  private registerUnloadEventListeners() {
    /** handler has to be defined as an arrow function to capture the correct "this" */
    const unloadHandler = () => {
      if (this.hasHandledUnload) {
        return;
      }

      this.hasHandledUnload = true;
      this.eventCallback(Event.UNLOAD, {
        currentTime: this.context.registry.get(SourceExportNames.SourceState).playback.playhead.position,
      });
      this.release();
    };

    this.context.abortSignal.addEventListener('abort', unloadHandler);
    this.windowEventTracker.addEventListener('beforeunload', unloadHandler);
    this.windowEventTracker.addEventListener('unload', unloadHandler);
  }

  private registerEventListeners() {
    // The context might not be same so a different source event bus is available
    const sourceEventBus = this.context.registry.get(SourcesApiExportNames.SourceEventBus);
    const playbackAtom = this.context.registry.get(SourceExportNames.SourceState).playback;
    const PlaybackEvent = this.context.registry.get(PlaybackApiEventsPackageExportNames.PlaybackEvent);

    sourceEventBus.on(PlaybackEvent.Ready, () => {
      this.eventCallback(Event.SOURCE_LOADED, {currentTime: playbackAtom.playhead.position});
    });
    sourceEventBus.on(PlaybackEvent.Play, () => {
      this.eventCallback(Event.PLAY, {currentTime: playbackAtom.playhead.position});
    });
    sourceEventBus.on(PlaybackEvent.Playing, () => {
      this.eventCallback(Event.PLAYING, {currentTime: playbackAtom.playhead.position});
    });
    sourceEventBus.on(PlaybackEvent.Paused, () => {
      this.eventCallback(Event.PAUSE, {currentTime: playbackAtom.playhead.position});
    });
    sourceEventBus.on(PlaybackEvent.TimeChanged, () => {
      this.eventCallback(Event.TIMECHANGED, {currentTime: playbackAtom.playhead.position});
    });
    sourceEventBus.on(PlaybackEvent.VideoAttached, () => {
      this.eventCallback(Event.SOURCE_LOADED, {currentTime: playbackAtom.playhead.position});
    });

    sourceEventBus.on(PlaybackEvent.Ended, () => {
      this.eventCallback(Event.END, {currentTime: playbackAtom.playhead.position});
    });

    const abortListener = () => {
      const abortSignalReason = this.context.abortSignal.reason;
      if (abortSignalReason.name === 'SourceRemovedError' || abortSignalReason.name === 'FrameworkDisposedError') {
        this.eventCallback(Event.SOURCE_UNLOADED, {currentTime: playbackAtom.playhead.position});
      } else {
        this.eventCallback(Event.ERROR, {
          currentTime: playbackAtom.playhead.position,
          code: PWX_ABORT_ERROR_CODE,
          message: abortSignalReason.message,
          data: abortSignalReason,
          originalError: abortSignalReason,
        });
      }

      this.context.abortSignal.removeEventListener('abort', abortListener);
    };

    this.context.abortSignal.addEventListener('abort', abortListener);
  }
}
